import React, { useState, useRef, useEffect } from 'react'
import '../styles/CreatorClipsSection.css'

function CreatorVodSection({ creators }) {
  // State to track which creators have expanded VODs
  const [expandedCreators, setExpandedCreators] = useState(new Set());
  const [visiblePlayers, setVisiblePlayers] = useState(new Set());
  
  // Number of VODs to show initially
  const INITIAL_VODS_COUNT = 2;

  // Intersection Observer for lazy loading
  const observerRef = useRef(null);

  useEffect(() => {
    // Create intersection observer for lazy loading
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const vodId = entry.target.dataset.vodId;
            if (vodId) {
              console.log('Loading VOD automatically:', vodId);
              setVisiblePlayers(prev => new Set([...prev, vodId]));
            }
          }
        });
      },
      {
        rootMargin: '200px', // Start loading 200px before the element is visible
        threshold: 0.1
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Auto-load first few VODs immediately when component mounts
  useEffect(() => {
    if (creators && Array.isArray(creators) && creators.length > 0) {
      const initialVodIds = new Set();
      creators.forEach(creator => {
        if (creator && creator.vods && Array.isArray(creator.vods)) {
          // Auto-load the first 2 VODs for each creator
          creator.vods.slice(0, 2).forEach(vod => {
            if (vod && vod.id) {
              initialVodIds.add(vod.id);
            }
          });
        }
      });
      
      if (initialVodIds.size > 0) {
        console.log('Auto-loading initial VODs:', Array.from(initialVodIds));
        setVisiblePlayers(initialVodIds);
      }
    }
  }, [creators]);

  // Add console logging for debugging
  console.log('CreatorVodSection received creators:', creators);
  console.log('Creators type:', typeof creators);
  console.log('Is array:', Array.isArray(creators));
  
  // Early return if creators is not valid - check for undefined first
  if (!creators || creators === undefined || creators === null) {
    console.log('Creators is null/undefined, showing loading state');
    return (
      <section className="vods-section">
        <div className="vods-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <h2 className="loading-title">Loading VODs...</h2>
            <p className="loading-description">Please wait while we fetch your data.</p>
          </div>
        </div>
      </section>
    );
  }

  // Check if creators is an array and has items
  if (!Array.isArray(creators) || creators.length === 0) {
    console.log('Creators is not an array or is empty:', creators);
    return (
      <section className="vods-section">
        <div className="vods-container">
          <div className="empty-state">
            <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h2 className="empty-title">No Creators Found</h2>
            <p className="empty-description">
              Try searching for some Twitch creators to get started!
            </p>
          </div>
        </div>
      </section>
    );
  }

  try {
    // Filter out invalid creators and check if any have VODs
    // Ensure creators is an array before calling filter
    if (!Array.isArray(creators)) {
      console.log('Creators is not an array, converting to empty array');
      creators = [];
    }
    
    const validCreators = creators.filter(creator => creator && creator.creator);
    console.log('Valid creators:', validCreators);
    
    if (!validCreators || validCreators.length === 0) {
      console.log('No valid creators found after filtering');
      return (
        <section className="vods-section">
          <div className="vods-container">
            <div className="empty-state">
              <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h2 className="empty-title">No Valid Creators Found</h2>
              <p className="empty-description">
                Try searching for some Twitch creators to get started!
              </p>
            </div>
          </div>
        </section>
      );
    }

    // Log each creator's structure for debugging
    validCreators.forEach((creator, index) => {
      console.log(`Creator ${index}:`, creator);
      console.log(`Creator ${index} vods:`, creator.vods);
      console.log(`Creator ${index} vods type:`, typeof creator.vods);
      console.log(`Creator ${index} vods is array:`, Array.isArray(creator.vods));
    });

    const hasAnyVods = validCreators.some(creator => creator && creator.vods && Array.isArray(creator.vods) && creator.vods.length > 0);

    if (!hasAnyVods) {
      console.log('No VODs found for any creators');
      return (
        <section className="vods-section">
          <div className="vods-container">
            <div className="empty-state">
              <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h2 className="empty-title">No VODs Found</h2>
              <p className="empty-description">
                The creators you searched for don't have any VODs available, or their VODs are private.
              </p>
              <p className="empty-subdescription">
                Try searching for different creators or check back later!
              </p>
            </div>
          </div>
        </section>
      );
    }

    // Function to handle expanding/collapsing VODs for a creator
    const toggleCreatorExpansion = (creatorName) => {
      const newExpandedCreators = new Set(expandedCreators);
      if (newExpandedCreators.has(creatorName)) {
        newExpandedCreators.delete(creatorName);
      } else {
        newExpandedCreators.add(creatorName);
      }
      setExpandedCreators(newExpandedCreators);
    };

    // Function to render VOD player with lazy loading
    const renderVodPlayer = (vod, creator) => {
      const vodId = vod.id;
      const isVisible = visiblePlayers.has(vodId);

      const handlePlaceholderClick = () => {
        console.log('Manual click to load VOD:', vodId);
        setVisiblePlayers(prev => new Set([...prev, vodId]));
      };

      return (
        <div 
          key={vodId} 
          className="vod-card"
          data-vod-id={vodId}
          ref={(el) => {
            if (el && observerRef.current) {
              console.log('Observing VOD:', vodId);
              observerRef.current.observe(el);
            }
          }}
        >
          <div className="vod-player">
            {isVisible ? (
              <iframe
                title={`${vod.title || 'Creator VOD'} by ${creator.creator}`}
                src={`https://player.twitch.tv/?video=v${vodId}&parent=${window.location.hostname}&autoplay=false&muted=true`}
                allowFullScreen
                className="vod-iframe"
                loading="lazy"
              />
            ) : (
              <div 
                className="vod-placeholder"
                onClick={handlePlaceholderClick}
                style={{ cursor: 'pointer' }}
              >
                <div className="placeholder-content">
                  <svg className="placeholder-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="placeholder-text">Video loading...</p>
                </div>
              </div>
            )}
          </div>
          <div className="vod-info">
            <h3 className="vod-title" title={vod.title || 'Untitled'}>
              {vod.title || 'Untitled'}
            </h3>
            <div className="vod-meta">
              <div className="vod-stat">
                <svg className="vod-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>{vod.view_count?.toLocaleString() || 0} views</span>
              </div>
              <div className="vod-stat">
                <svg className="vod-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{vod.duration || 'Unknown'}</span>
              </div>
            </div>
            <div className="vod-date">
              {vod.created_at ? new Date(vod.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              }) : 'Unknown date'}
            </div>
          </div>
        </div>
      );
    };

    return (
      <section className="vods-section">
        <div className="vods-container">
          {validCreators && Array.isArray(validCreators) && validCreators.map(creator => {
            // Double-check creator exists (should be filtered already, but safety first)
            if (!creator || !creator.creator) {
              console.warn('Invalid creator object:', creator);
              return null;
            }

            // Ensure vods is an array before trying to map over it
            const vods = creator.vods && Array.isArray(creator.vods) ? creator.vods : [];
            console.log(`Creator ${creator.creator} has ${vods.length} VODs`);

            // Check if this creator's VODs are expanded
            const isExpanded = expandedCreators.has(creator.creator);
            
            // Determine how many VODs to show
            const vodsToShow = isExpanded ? vods : vods.slice(0, INITIAL_VODS_COUNT);
            const hasMoreVods = vods.length > INITIAL_VODS_COUNT;
            const remainingVodsCount = vods.length - INITIAL_VODS_COUNT;

            return (
              <div key={creator.creator} className="creator-section">
                <div className="creator-header">
                  <h2 className="creator-name">{creator.creator}</h2>
                  <span className="vod-count">{vods.length} VOD{vods.length !== 1 ? 's' : ''}</span>
                </div>
                
                <div className="vods-grid">
                  {vods.length > 0 ? (
                    vodsToShow.map(vod => {
                      // Ensure vod and vod.id exist
                      if (!vod || !vod.id) {
                        console.warn('Invalid VOD object:', vod);
                        return null;
                      }

                      return renderVodPlayer(vod, creator);
                    }).filter(Boolean) // Remove any null values
                  ) : (
                    <div className="no-vods-card">
                      <div className="no-vods-content">
                        <svg className="no-vods-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <h3 className="no-vods-title">No VODs Available</h3>
                        <p className="no-vods-description">
                          This creator doesn't have any public VODs or they may be private.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Show "See more VODs" button if there are more VODs */}
                {hasMoreVods && (
                  <div className="see-more-container">
                    <button
                      className="see-more-button"
                      onClick={() => toggleCreatorExpansion(creator.creator)}
                    >
                      {isExpanded ? (
                        <>
                          <svg className="see-more-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                          Show Less
                        </>
                      ) : (
                        <>
                          <svg className="see-more-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          See {remainingVodsCount} More VOD{remainingVodsCount !== 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  } catch (error) {
    console.error('Error in CreatorVodSection:', error);
    return (
      <section className="vods-section">
        <div className="vods-container">
          <div className="error-state">
            <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="error-title">Something went wrong</h2>
            <p className="error-description">
              Please try refreshing the page or searching again.
            </p>
            <p className="error-details">Error: {error.message}</p>
          </div>
        </div>
      </section>
    );
  }
}

export default CreatorVodSection